import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { toursAPI } from '../lib/api';
import { toast } from 'sonner';
import { formatPrice, getCategoryColor, getCategoryLabel } from '../lib/utils';
import { 
  Star, Clock, MapPin, Check, 
  ArrowLeft, Loader2, ExternalLink, ChevronLeft, ChevronRight,
  CalendarDays, Users, Minus, Plus, Sparkles, Shield, Award, Share2
} from 'lucide-react';
import { 
  LiveViewers, 
  RecentBookings, 
  BookAheadBadge, 
  RecentBookingToast, 
  UrgencyStrip,
  LimitedAvailabilityBadge
} from '../components/tours/UrgencyBadges';
import { ShareButtons, InlineShareButtons } from '../components/tours/ShareButtons';
import { ReviewsSection } from '../components/tours/ReviewsSection';
import { BookingModal } from '../components/booking/BookingModal';
import { format, parseISO, isSameDay } from 'date-fns';

// Simple Image Gallery Component
function ImageGallery({ images }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const thumbnailRef = useRef(null);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Scroll thumbnail into view when current index changes
  useEffect(() => {
    if (thumbnailRef.current) {
      const thumbnail = thumbnailRef.current.children[currentIndex];
      if (thumbnail) {
        thumbnail.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [currentIndex]);

  if (!images || images.length === 0) return null;

  return (
    <div className="relative">
      {/* Main Image */}
      <div className="relative aspect-[4/3] md:aspect-[16/9] overflow-hidden bg-muted">
        <img
          src={images[currentIndex]}
          alt={`Tour image ${currentIndex + 1}`}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = 'https://via.placeholder.com/800x600?text=Image'; }}
        />
        
        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-5 w-5 text-gray-800" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
              aria-label="Next image"
            >
              <ChevronRight className="h-5 w-5 text-gray-800" />
            </button>
          </>
        )}

        {/* Image Counter */}
        <div className="absolute top-3 right-3 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-medium">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Thumbnail Strip */}
      {images.length > 1 && (
        <div 
          ref={thumbnailRef}
          className="flex gap-2 mt-2 overflow-x-auto pb-2 px-1 scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {images.map((image, index) => (
            <button
              key={`thumb-${image}-${index}`}
              onClick={() => goToSlide(index)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                index === currentIndex 
                  ? 'border-primary ring-2 ring-primary/30' 
                  : 'border-transparent opacity-70 hover:opacity-100'
              }`}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/100?text=Img'; }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Tour Card for Recommendations
function TourMiniCard({ tour }) {
  // Generate consistent random for this tour
  const getConsistentRandom = (seed, min, max) => {
    const hash = (tour.id + seed).split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return min + Math.abs(hash) % (max - min + 1);
  };

  const bookingsLast24h = getConsistentRandom('mini', 3, 12);
  const isPopular = tour.rating >= 4.7 || bookingsLast24h >= 8;

  return (
    <Link to={`/tours/${tour.id}`} className="flex-shrink-0 w-64">
      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
        <div className="relative aspect-[4/3]">
          <img
            src={tour.image_url}
            alt={tour.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Tour'; }}
          />
          <Badge className={`absolute top-2 left-2 ${getCategoryColor(tour.category)} border-0 text-xs`}>
            {getCategoryLabel(tour.category).replace(' Tours', '')}
          </Badge>
          {isPopular && (
            <div className="absolute top-2 right-2 bg-orange-500 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
              Popular
            </div>
          )}
          <div className="absolute bottom-2 right-2 bg-secondary text-secondary-foreground px-2 py-1 rounded-full text-sm font-bold">
            {formatPrice(tour.price)}
          </div>
        </div>
        <div className="p-3">
          <h3 className="font-semibold text-sm line-clamp-2 mb-1">{tour.title}</h3>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{tour.duration}</span>
            </div>
            <div className="flex items-center gap-1 text-green-600 font-medium">
              <Check className="h-3 w-3" />
              <span>Free Cancel</span>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export default function TourDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [tour, setTour] = useState(null);
  const [popularTours, setPopularTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBookingToast, setShowBookingToast] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const scrollContainerRef = useRef(null);
  
  // Inline booking state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [guests, setGuests] = useState(1);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [timeSlotsForDate, setTimeSlotsForDate] = useState([]);
  const [bookingSettings, setBookingSettings] = useState({
    bookingStartTime: '09:00',
    bookingEndTime: '17:00',
    maxGuestsPerDay: 20
  });
  const [blockedDates, setBlockedDates] = useState([]);

  // Scroll to top when page loads or tour ID changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [tourRes, toursRes, availRes] = await Promise.all([
          toursAPI.getById(id),
          toursAPI.getAll(),
          toursAPI.getAvailabilitySlots(id).catch(() => ({ data: { slots: [], settings: {} } }))
        ]);
        setTour(tourRes.data);
        // Filter out current tour and take 6 random ones
        const otherTours = toursRes.data.filter(t => t.id !== id);
        setPopularTours(otherTours.slice(0, 6));
        
        // Process availability slots
        const slots = availRes.data?.slots || [];
        setAvailabilitySlots(slots);
        const uniqueDates = [...new Set(slots.filter(s => s.is_available).map(s => s.date))];
        setAvailableDates(uniqueDates.map(d => parseISO(d)));
        
        // Store booking settings from admin
        if (availRes.data?.settings) {
          setBookingSettings(availRes.data.settings);
        }
        
        // Store blocked dates
        if (availRes.data?.blocked_dates) {
          setBlockedDates(availRes.data.blocked_dates);
        }
      } catch (error) {
        console.error('Error loading tour:', error);
        toast.error('Failed to load tour details');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);
  
  // Check availability for selected date
  useEffect(() => {
    if (selectedDate && availabilitySlots.length > 0) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      // Find the slot for this date
      const slotForDate = availabilitySlots.find(
        slot => slot.date === dateStr && slot.is_available !== false
      );
      
      if (slotForDate) {
        // Store slot info for availability display
        setTimeSlotsForDate([slotForDate]);
      } else {
        setTimeSlotsForDate([]);
      }
    }
  }, [selectedDate, availabilitySlots]);

  const handleBookNow = () => {
    if (tour?.fareharbor_url) {
      // External booking via FareHarbor
      window.open(tour.fareharbor_url, '_blank');
    } else {
      // Internal booking system - pass pre-selected date/time/guests
      setShowBookingModal(true);
    }
  };
  
  // Calculate dynamic price
  const calculateTotalPrice = () => {
    const basePrice = tour?.price || 0;
    return basePrice * guests;
  };
  
  // Format time to AM/PM
  const formatTimeToAMPM = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };
  
  // Check if date has available slots
  const isDateAvailable = (date) => {
    return availableDates.some(d => isSameDay(d, date));
  };

  const scrollTours = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 280;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-semibold">Tour not found</h1>
          <Button onClick={() => navigate('/tours')} className="mt-4">
            Back to Tours
          </Button>
        </div>
      </div>
    );
  }

  // Get gallery images
  const galleryImages = tour?.images?.length > 0 ? tour.images : [tour?.image_url];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 py-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      {/* Image Gallery */}
      <section className="px-4 mb-4" data-testid="tour-gallery">
        <div className="max-w-4xl mx-auto">
          <ImageGallery images={galleryImages} />
        </div>
      </section>

      {/* Tour Info Card */}
      <section className="px-4 mb-4">
        <div className="max-w-4xl mx-auto">
          <Card className="p-5 rounded-2xl shadow-lg">
            {/* Urgency Badges */}
            <div className="mb-3">
              <UrgencyStrip tour={tour} />
            </div>

            {/* Title & Category Row */}
            <div className="flex items-start justify-between mb-2">
              <div>
                <Badge className={`${getCategoryColor(tour.category)} border-0 mb-2`}>
                  {getCategoryLabel(tour.category)}
                </Badge>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground" data-testid="tour-title">
                  {tour.title}
                </h1>
              </div>
              <ShareButtons tour={tour} />
            </div>
            
            {/* Quick Info */}
            <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-3">
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-secondary text-secondary" />
                <span className="font-semibold text-foreground">{tour.rating}</span>
                <span>({tour.reviews_count})</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{tour.duration}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                <span>{tour.location}</span>
              </div>
            </div>

            {/* Social Proof Stats */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 py-3 border-y border-border mb-3">
              <LiveViewers tour={tour} />
              <RecentBookings tour={tour} />
            </div>

            {/* Dynamic Price Display */}
            <div className="flex items-center justify-between pt-2">
              <div>
                <p className="text-xs text-muted-foreground">
                  {guests > 1 ? `${guests} guests × ${formatPrice(tour.price)}` : 'From'}
                </p>
                <p className="font-heading text-2xl font-bold text-primary" data-testid="dynamic-price">
                  {formatPrice(calculateTotalPrice())}
                </p>
                {guests > 1 && (
                  <p className="text-xs text-muted-foreground">Total for {guests} guests</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <BookAheadBadge tour={tour} />
                {tour.fareharbor_url ? (
                  <Button 
                    className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-6 py-2 font-semibold"
                    onClick={handleBookNow}
                    data-testid="book-now-top-btn"
                  >
                    Book Now
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-6 py-2 font-semibold"
                    onClick={() => document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' })}
                    data-testid="book-now-top-btn"
                  >
                    Select Date & Book
                    <CalendarDays className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Main Content - Two Column Layout */}
      <section className="py-4 px-4" data-testid="tour-content">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left Column - Tour Details */}
            <div className="lg:col-span-3 space-y-5">
              {/* Custom Features - Tour Guarantees/Perks */}
              {tour.custom_features?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="tour-features">
                  {tour.custom_features.map((feature, index) => {
                    const iconMap = {
                      shield: '🛡️',
                      clock: '⏰',
                      star: '⭐',
                      check: '✓',
                      car: '🚗',
                      utensils: '🍽️',
                      camera: '📷',
                      map: '📍',
                      users: '👥',
                      heart: '❤️',
                    };
                    return (
                      <Card key={`feature-${feature.text}-${index}`} className="p-4 flex items-start gap-3">
                        <span className="text-2xl">{iconMap[feature.icon] || '✓'}</span>
                        <div>
                          <h4 className="font-semibold text-sm">{feature.text}</h4>
                          <p className="text-xs text-muted-foreground">{feature.description}</p>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Highlights - Enhanced with gradient border and icon */}
              {tour.show_highlights !== false && tour.highlights?.length > 0 && (
                <Card className="p-5 rounded-2xl border-2 border-amber-200/60 bg-gradient-to-br from-amber-50/80 to-orange-50/40 shadow-md" data-testid="tour-highlights">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Sparkles className="h-5 w-5 text-amber-600" />
                    </div>
                    <h2 className="font-heading text-xl font-bold text-amber-800">Highlights</h2>
                  </div>
                  <ul className="space-y-2">
                    {tour.highlights.map((highlight, index) => (
                      <li key={`highlight-${highlight.substring(0, 20)}-${index}`} className="flex items-start gap-3 p-3 bg-white/70 rounded-lg border border-amber-100">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                          <Check className="h-3.5 w-3.5 text-white" />
                        </div>
                        <span className="text-sm font-medium text-gray-700">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* Description - Enhanced with accent styling */}
              {tour.show_description !== false && (
                <Card className="p-5 rounded-2xl border-2 border-blue-200/60 bg-gradient-to-br from-blue-50/80 to-sky-50/40 shadow-md" data-testid="tour-description">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <MapPin className="h-5 w-5 text-blue-600" />
                    </div>
                    <h2 className="font-heading text-xl font-bold text-blue-800">About This Tour</h2>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-base">{tour.description}</p>
                </Card>
              )}

              {/* What's Included - Enhanced with green theme */}
              {tour.show_included !== false && tour.included?.length > 0 && (
                <Card className="p-5 rounded-2xl border-2 border-green-200/60 bg-gradient-to-br from-green-50/80 to-emerald-50/40 shadow-md" data-testid="tour-included">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Shield className="h-5 w-5 text-green-600" />
                    </div>
                    <h2 className="font-heading text-xl font-bold text-green-800">What&apos;s Included</h2>
                  </div>
                  <ul className="space-y-2">
                    {tour.included.map((item, index) => (
                      <li key={`included-${item.substring(0, 20)}-${index}`} className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-green-100">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                          <Check className="h-3.5 w-3.5 text-white" />
                        </div>
                        <span className="text-sm font-medium text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* What to Bring - Enhanced with blue theme */}
              {tour.show_what_to_bring !== false && tour.what_to_bring?.length > 0 && (
                <Card className="p-5 rounded-2xl border-2 border-blue-200/60 bg-gradient-to-br from-blue-50/80 to-sky-50/40 shadow-md" data-testid="tour-what-to-bring">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Award className="h-5 w-5 text-blue-600" />
                    </div>
                    <h2 className="font-heading text-xl font-bold text-blue-800">What to Bring</h2>
                  </div>
                  <ul className="space-y-2">
                    {tour.what_to_bring.map((item, index) => (
                      <li key={`bring-${item.substring(0, 20)}-${index}`} className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-blue-100">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                          <Check className="h-3.5 w-3.5 text-white" />
                        </div>
                        <span className="text-sm font-medium text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>

            {/* Right Column - Booking Section (Sticky) */}
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-24">
                {/* Inline Booking for Internal Tours */}
                {!tour.fareharbor_url ? (
                  <Card id="booking-section" className="p-5 rounded-2xl shadow-lg bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 border-2 border-primary/20" data-testid="inline-booking-section">
                    <h2 className="font-heading text-xl font-bold mb-5 text-center">Book Your Adventure</h2>
                    
                    {/* Step 1: Select Date */}
                    <div className="mb-5">
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${
                          selectedDate ? 'bg-green-500 text-white' : 'bg-primary text-white'
                        }`}>
                          {selectedDate ? <Check className="h-3.5 w-3.5" /> : '1'}
                        </div>
                        <h3 className="font-semibold text-sm">Select a Date</h3>
                        {selectedDate && (
                          <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                            {format(selectedDate, 'MMM d')}
                          </Badge>
                        )}
                      </div>
                      <div className="border rounded-xl p-2 bg-white">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return date < today || (availableDates.length > 0 && !isDateAvailable(date));
                          }}
                          modifiers={{
                            available: (date) => {
                              // Only mark as available if not selected
                              if (selectedDate && date.getTime() === selectedDate.getTime()) return false;
                              return isDateAvailable(date);
                            }
                          }}
                          modifiersStyles={{
                            available: { backgroundColor: '#dcfce7', color: '#166534' }
                          }}
                          modifiersClassNames={{
                            selected: 'bg-green-600 text-white font-bold hover:bg-green-700'
                          }}
                          className="rounded-md w-full [&_.rdp-day_disabled]:bg-transparent [&_.rdp-day_disabled]:text-gray-300 [&_.rdp-day_selected]:bg-green-600 [&_.rdp-day_selected]:text-white [&_.rdp-day_selected]:font-bold"
                        />
                      </div>
                      
                      {/* Availability Info */}
                      {selectedDate && timeSlotsForDate.length > 0 && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-800 font-medium">
                            ✓ {timeSlotsForDate[0].available_spots} spots available for {format(selectedDate, 'MMM d')}
                          </p>
                        </div>
                      )}
                      {selectedDate && timeSlotsForDate.length === 0 && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-800">
                            No availability for this date. Please select another date.
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Step 2: Select Guests */}
                    <div className={`mb-5 ${!selectedDate || timeSlotsForDate.length === 0 ? 'opacity-50 pointer-events-none' : ''}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${
                          selectedDate && timeSlotsForDate.length > 0 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                        }`}>
                          {selectedDate && timeSlotsForDate.length > 0 ? <Check className="h-3.5 w-3.5" /> : '2'}
                        </div>
                        <h3 className="font-semibold text-sm">How Many Guests?</h3>
                      </div>
                      <div className="flex items-center gap-4 p-3 border rounded-xl bg-white">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setGuests(Math.max(1, guests - 1))}
                          disabled={guests <= 1}
                          className="h-9 w-9 rounded-full"
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <div className="flex-1 text-center">
                          <span className="text-3xl font-bold">{guests}</span>
                          <p className="text-xs text-muted-foreground">{guests === 1 ? 'Guest' : 'Guests'}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setGuests(Math.min(timeSlotsForDate[0]?.available_spots || 20, guests + 1))}
                          disabled={guests >= (timeSlotsForDate[0]?.available_spots || 20)}
                          className="h-9 w-9 rounded-full"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Step 3: Review & Book */}
                    <div className={`${!selectedDate || timeSlotsForDate.length === 0 ? 'opacity-50 pointer-events-none' : ''}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${
                          selectedDate && timeSlotsForDate.length > 0 ? 'bg-primary text-white' : 'bg-gray-300 text-gray-600'
                        }`}>
                          3
                        </div>
                        <h3 className="font-semibold text-sm">Review & Book</h3>
                      </div>
                      
                      {/* Price Summary */}
                      <div className="p-4 bg-white rounded-xl border-2 border-secondary/30 mb-3">
                        <div className="space-y-1 text-sm mb-3">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">{formatPrice(tour.price)} × {guests}</span>
                            <span className="font-medium">{formatPrice(calculateTotalPrice())}</span>
                          </div>
                          {selectedDate && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Date</span>
                              <span className="font-medium">{format(selectedDate, 'EEEE, MMM d, yyyy')}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-between items-center text-lg font-bold pt-2 border-t">
                          <span>Total</span>
                          <span className="text-primary" data-testid="total-price">{formatPrice(calculateTotalPrice())}</span>
                        </div>
                      </div>
                      
                      {/* Book Button */}
                      <Button 
                        className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 py-5 text-base font-semibold rounded-xl shadow-lg"
                        onClick={handleBookNow}
                        disabled={availableDates.length > 0 && (!selectedDate || timeSlotsForDate.length === 0)}
                        data-testid="proceed-booking-btn"
                      >
                        {availableDates.length > 0 && (!selectedDate || timeSlotsForDate.length === 0)
                          ? 'Select a Date'
                          : `Continue - ${formatPrice(calculateTotalPrice())}`}
                      </Button>
                      {/* Trust Badges */}
                      <div className="flex items-center justify-center gap-3 mt-3 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-500" />
                          Free Cancellation
                        </span>
                        <span className="flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-500" />
                          Instant Confirmation
                        </span>
                        <span className="flex items-center gap-1">
                          <Award className="h-3 w-3 text-amber-500" />
                          Best Price Guarantee
                        </span>
                      </div>
                    </div>
                  </Card>
                ) : (
                  /* FareHarbor External Booking Card */
                  <Card className="p-5 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/20 border-2 border-primary/30 shadow-lg" data-testid="book-now-section">
                    <div className="text-center">
                      <LimitedAvailabilityBadge tour={tour} />
                      <h2 className="font-heading text-xl font-semibold mb-2">Book Your Adventure</h2>
                      <p className="text-muted-foreground text-sm mb-4">
                        Starting from <span className="font-bold text-primary text-xl">{formatPrice(tour.price)}</span> per person
                      </p>
                      <Button 
                        className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-xl px-6 py-5 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                        onClick={handleBookNow}
                        data-testid="book-now-btn"
                      >
                        Book Now - Secure Your Spot
                        <ExternalLink className="h-5 w-5 ml-2" />
                      </Button>
                      <p className="text-xs text-muted-foreground mt-3">
                        Free cancellation up to 24 hours before
                      </p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section - Full Width */}
      <section className="py-4 px-4">
        <div className="max-w-6xl mx-auto">
          <ReviewsSection tour={tour} />
        </div>
      </section>

      {/* Referral Agent CTA */}
      <section className="py-6 px-4 bg-gradient-to-r from-primary/5 to-secondary/5">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Share2 className="h-5 w-5 text-primary" />
            <h3 className="font-semibold text-lg">Earn by Sharing</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Love this tour? Become a referral agent and earn commissions for every booking you refer.
          </p>
          <Button asChild variant="outline" className="gap-2">
            <Link to="/become-referral-agent">
              <Share2 className="h-4 w-4" />
              Become a Referral Agent
            </Link>
          </Button>
        </div>
      </section>

      {/* Recent Booking Toast Notification */}
      {showBookingToast && tour && (
        <RecentBookingToast 
          tour={tour} 
          onClose={() => setShowBookingToast(false)} 
        />
      )}

      {/* More Popular Tours Slider */}
      {popularTours.length > 0 && (
        <section className="py-8 bg-muted/30" data-testid="popular-tours-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-semibold">More Popular Tours</h2>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={() => scrollTours('left')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={() => scrollTours('right')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div 
              ref={scrollContainerRef}
              className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {popularTours.map((popularTour) => (
                <TourMiniCard key={popularTour.id} tour={popularTour} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />

      {/* Booking Modal - Internal Booking System */}
      {tour && !tour.fareharbor_url && (
        <BookingModal
          tour={tour}
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          preSelectedDate={selectedDate}
          preSelectedTime={bookingSettings.bookingStartTime || '09:00'}
          preSelectedGuests={guests}
        />
      )}
    </div>
  );
}
